import React, { useState, useEffect, useRef } from "react";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { useHistoricLeaderboard } from "../hooks/useHistoricLeaderboard";
import { useAuth } from "../contexts/AppKitAuthContext";
import styles from "../styles/WeeklyLeaderboard.module.css";
import toggleStyles from "../styles/ToggleButton.module.css";
import RewardDistributionStatus from "./RewardDistributionStatus";

interface WeeklyLeaderboardProps {
  className?: string;
  isActive?: boolean;
  onRecoverStreak?: () => void;
}

type LeaderboardTab = "gameCount" | "dxfMining";

const WeeklyLeaderboard: React.FC<WeeklyLeaderboardProps> = ({ className = "", isActive = true, onRecoverStreak }) => {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>("gameCount");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showStickyUserRank, setShowStickyUserRank] = useState(false);
  const [showStickyUserTop, setShowStickyUserTop] = useState(false);

  // Historic week data
  const {
    currentWeekData: historicData,
    loading: historicLoading,
    error: historicError,
    selectWeek,
    clearWeek,
    availableWeeks,
    goToPreviousWeek,
    goToNextWeek,
    canGoToPrevious,
    canGoToNext,
    getWeekDisplayString: historicWeekDisplayString,
  } = useHistoricLeaderboard();

  // Current week data
  const {
    data: currentData,
    userStreak,
    loading: currentLoading,
    error: currentError,
    formatWalletAddress,
    formatDXFAmount,
    formatRewardAmount,
    getWeekDisplayString: currentWeekDisplayString,
    refetch,
  } = useLeaderboard(isActive && !historicData?.weekStartDate);

  const { isAuthenticated, walletAddress } = useAuth();

  // Determine if we're in historic mode
  const isHistoricMode = Boolean(historicData?.weekStartDate);

  // Select appropriate data based on mode
  const data = isHistoricMode ? historicData : currentData;
  const loading = isHistoricMode ? historicLoading : currentLoading;
  const error = isHistoricMode ? historicError : currentError;
  const getWeekDisplayString = isHistoricMode ? historicWeekDisplayString : currentWeekDisplayString;

  const leaderboardContentRef = useRef<HTMLDivElement>(null);
  const userEntryRef = useRef<HTMLDivElement>(null);

  // Trigger refresh and scroll check when component becomes active
  useEffect(() => {
    if (isActive && data && !loading) {
      console.log("[DEBUG] Component became active, triggering scroll check");
      setTimeout(() => {
        if (leaderboardContentRef.current) {
          const event = new Event("scroll");
          leaderboardContentRef.current.dispatchEvent(event);
        }
      }, 100);
    }
  }, [isActive, data, loading]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (!isHistoricMode) {
        await refetch();
      } else if (historicData?.weekStartDate) {
        selectWeek(historicData.weekStartDate);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePreviousWeek = () => {
    if (!isHistoricMode && availableWeeks.length > 0) {
      // Switch to historic mode and select the most recent completed week
      selectWeek(availableWeeks[0].weekStartDate);
    } else {
      goToPreviousWeek();
    }
  };

  const handleNextWeek = () => {
    if (isHistoricMode) {
      if (availableWeeks.length > 0 && historicData?.weekStartDate === availableWeeks[0].weekStartDate) {
        // We're on the latest historic week, switch back to current mode
        clearWeek();
      } else if (canGoToNext) {
        // We can go to a newer historic week
        goToNextWeek();
      }
    }
  };

  // Create enhanced ranking data that includes user if not in top 50
  const getEnhancedRanking = () => {
    if (!data) return [];

    const currentRanking = activeTab === "gameCount" ? data.gameCountRanking : data.dxfMiningRanking;

    // Check if user is authenticated and has ranking data
    if (!isAuthenticated || !data.userRank || !walletAddress) return currentRanking;

    // Check if user is already in the ranking
    const userInRanking = currentRanking.some(
      (entry) => entry.walletAddress.toLowerCase() === walletAddress.toLowerCase()
    );

    // If user is already in top 50, return as is
    if (userInRanking) return currentRanking;

    // Get user's entry from the embedded userRank data
    const userEntry = activeTab === "gameCount" ? data.userRank.gameCount : data.userRank.dxfMining;

    // Add user's entry to the end if they have a valid rank (including rank 0 which means not in top ranking)
    if (userEntry && userEntry.rank >= 0) {
      return [...currentRanking, userEntry];
    }

    return currentRanking;
  };

  // Scroll detection to show/hide sticky user rank
  useEffect(() => {
    if (!isActive) return; // Skip if component is not active

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (!userEntryRef.current || !leaderboardContentRef.current) {
            ticking = false;
            return;
          }

          const userEntry = userEntryRef.current;
          const container = leaderboardContentRef.current;
          const containerRect = container.getBoundingClientRect();
          const userEntryRect = userEntry.getBoundingClientRect();

          // Check if user entry is visible in viewport with some tolerance for smoother transitions
          const tolerance = 10;
          const isUserEntryVisible =
            userEntryRect.bottom <= containerRect.bottom + tolerance &&
            userEntryRect.top >= containerRect.top - tolerance;

          const hasValidRank = Boolean(
            isAuthenticated &&
              data?.userRank &&
              ((data.userRank.gameCount?.rank !== null &&
                data.userRank.gameCount?.rank !== undefined &&
                data.userRank.gameCount?.rank >= 0) ||
                (data.userRank.dxfMining?.rank !== null &&
                  data.userRank.dxfMining?.rank !== undefined &&
                  data.userRank.dxfMining?.rank >= 0))
          );

          if (!hasValidRank) {
            setShowStickyUserRank(false);
            setShowStickyUserTop(false);
            ticking = false;
            return;
          }

          // Determine sticky behavior based on user entry position relative to viewport
          const isUserEntryAboveViewport = userEntryRect.top < containerRect.top - tolerance;
          const isUserEntryBelowViewport = userEntryRect.bottom > containerRect.bottom + tolerance;

          if (!isUserEntryVisible) {
            // User entry is not visible in viewport
            if (isUserEntryAboveViewport) {
              // User entry is above viewport - show sticky at top
              setShowStickyUserTop(true);
              setShowStickyUserRank(false);
            } else if (isUserEntryBelowViewport) {
              // User entry is below viewport - show sticky at bottom
              setShowStickyUserTop(false);
              setShowStickyUserRank(true);
            }
          } else {
            // User entry is visible - hide both sticky displays
            setShowStickyUserTop(false);
            setShowStickyUserRank(false);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    const contentElement = leaderboardContentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll, { passive: true });
      // Check initial state
      handleScroll();

      return () => {
        contentElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isActive, isAuthenticated, data?.userRank, activeTab]);

  // Helper function to format rank display
  const formatRank = (rank: number): string => {
    if (rank === 0) return ">100";
    return `#${rank}`;
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  // Helper component for sticky user rank display
  const renderStickyUserEntry = (className: string) => {
    if (!data?.userRank || !walletAddress) return null;

    const userEntry = activeTab === "gameCount" ? data.userRank.gameCount : data.userRank.dxfMining;

    if (!userEntry) return null;

    return (
      <div className={className}>
        <div
          className={`${styles.leaderboardEntry} ${styles.currentUser} ${
            userEntry.rank <= 3 ? styles[`rank${userEntry.rank}`] : ""
          }`}
        >
          <div className={styles.rankCol}>
            <span className={styles.rankNumber}>
              {userEntry.rank <= 3 && userEntry.rank > 0 ? (
                <>
                  {userEntry.rank === 1 && "🥇"}
                  {userEntry.rank === 2 && "🥈"}
                  {userEntry.rank === 3 && "🥉"}
                  <span className={styles.rankText}>#{userEntry.rank}</span>
                </>
              ) : (
                formatRank(userEntry.rank)
              )}
            </span>
          </div>
          <div className={styles.addressCol}>
            <span className={styles.walletAddress}>{formatWalletAddress(walletAddress)}</span>
            <span className={styles.currentUserBadge}>YOU</span>
          </div>
          <div className={styles.scoreCol}>
            <span className={styles.scoreValue}>
              {activeTab === "gameCount"
                ? (userEntry.finalScore as number).toFixed(2)
                : formatDXFAmount(userEntry.finalScore as string)}
            </span>
            {userEntry.multiplier > 1 && (
              <span className={styles.multiplierBadge}>{userEntry.multiplier.toFixed(2)}x</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const enhancedRanking = getEnhancedRanking();

  if (loading) {
    return (
      <div className={`${styles.weeklyLeaderboard} ${className}`}>
        <div className={styles.leaderboardLoading}>
          <div className={styles.leaderboardLoadingSpinner}></div>
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.weeklyLeaderboard} ${className}`}>
        <div className={styles.leaderboardError}>
          <p>Failed to load leaderboard</p>
          <span className={styles.errorDetail}>{error}</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`${styles.weeklyLeaderboard} ${className}`}>
        <div className={styles.leaderboardEmpty}>
          <p>No leaderboard data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.weeklyLeaderboard} ${className}`}>
      {/* Header */}
      <div className={styles.leaderboardHeader}>
        <div className={styles.titleRow}>
          <h2 className={styles.leaderboardTitle}>Weekly Leaderboard</h2>
          <button
            className={`${toggleStyles.toggleButton} ${styles.refreshButton} ${isRefreshing ? styles.refreshing : ""}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh leaderboard"
            aria-label="Refresh leaderboard"
          >
            <svg
              className={`${isRefreshing ? styles.spinning : ""}`}
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
          </button>
        </div>

        <div className={styles.weekRange}>
          <button
            className={`${styles.navButton} ${
              (!isHistoricMode && availableWeeks.length === 0) || (isHistoricMode && !canGoToPrevious)
                ? styles.disabled
                : ""
            }`}
            onClick={handlePreviousWeek}
            disabled={(!isHistoricMode && availableWeeks.length === 0) || (isHistoricMode && !canGoToPrevious)}
            title="Previous week"
            aria-label="Previous week"
          >
            &lt;
          </button>
          <span>{getWeekDisplayString}</span>
          <button
            className={`${styles.navButton} ${
              !isHistoricMode ||
              (!canGoToNext &&
                !(availableWeeks.length > 0 && historicData?.weekStartDate === availableWeeks[0].weekStartDate))
                ? styles.disabled
                : ""
            }`}
            onClick={handleNextWeek}
            disabled={
              !isHistoricMode ||
              (!canGoToNext &&
                !(availableWeeks.length > 0 && historicData?.weekStartDate === availableWeeks[0].weekStartDate))
            }
            title={!isHistoricMode ? "" : canGoToNext ? "Next week" : "Current week"}
            aria-label={!isHistoricMode ? "" : canGoToNext ? "Next week" : "Current week"}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* User Streak Display (only for current week when authenticated) */}
      {((isAuthenticated && userStreak) || !isAuthenticated) && !isHistoricMode && (
        <div className={styles.userStreakSection}>
          <div className={styles.streakDisplay}>
            <span className={styles.streakLabel}>Your Streak:</span>
            <span className={styles.streakValue}>
              {userStreak?.currentStreak || "0"}/30{" "}
              <span className={styles.streakMultiplier}>{userStreak?.multiplier.toFixed(2) || "1.00"}x</span>
            </span>
            {/* Recovery Button or Info Tips */}
            {userStreak?.eligibleToRecover && userStreak?.lastStreak > 0 && onRecoverStreak ? (
              <button className={styles.recoverButton} onClick={onRecoverStreak}>
                Recover
              </button>
            ) : (
              <div className={styles.streakInfoTrigger}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M8 8a3.5 3 0 0 1 3.5 -3h1a3.5 3 0 0 1 3.5 3a3 3 0 0 1 -2 3a3 4 0 0 0 -2 4" />
                  <path d="M12 19l0 .01" />
                </svg>
                <div className={styles.streakTooltip}>
                  <div className={styles.streakTooltipContent}>
                    <p>Play daily to build and maintain your streak up to 30 days.</p>
                    <p>Each day adds +2% multiplier to your leaderboard score.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.leaderboardTabs}>
        <button
          className={`${styles.tab} ${activeTab === "gameCount" ? styles.active : ""}`}
          onClick={() => setActiveTab("gameCount")}
        >
          🎮 Game Count Rankings
        </button>
        <button
          className={`${styles.tab} ${activeTab === "dxfMining" ? styles.active : ""}`}
          onClick={() => setActiveTab("dxfMining")}
        >
          💰 DXF Mining Rankings
        </button>
      </div>

      {/* Leaderboard Content */}
      <div className={styles.leaderboardContent} ref={leaderboardContentRef}>
        {/* Sticky User Rank Top (shown when user is in top rankings but scrolled past) */}
        {showStickyUserTop && renderStickyUserEntry(styles.stickyUserTop)}

        {/* Table Header */}
        <div className={styles.leaderboardHeaderRow}>
          <div className={styles.rankCol}>Rank</div>
          <div className={styles.addressCol}>Address</div>
          <div className={styles.scoreCol}>{activeTab === "gameCount" ? "Games" : "DXF Mined"}</div>
        </div>

        {/* Leaderboard Entries */}
        <div className={styles.leaderboardEntries}>
          {enhancedRanking.length > 0 ? (
            enhancedRanking.map((entry, index) => {
              const isCurrentUser =
                isAuthenticated && walletAddress && entry.walletAddress.toLowerCase() === walletAddress.toLowerCase();

              return (
                <div
                  key={`${entry.walletAddress}-${entry.rank}`}
                  ref={isCurrentUser ? userEntryRef : undefined}
                  className={`${styles.leaderboardEntry} ${isCurrentUser ? styles.currentUser : ""} ${
                    entry.rank <= 3 ? styles[`rank${entry.rank}`] : ""
                  }`}
                >
                  <div className={styles.rankCol}>
                    <span className={styles.rankNumber}>
                      {entry.rank === 1 && "🥇"}
                      {entry.rank === 2 && "🥈"}
                      {entry.rank === 3 && "🥉"}
                      <span className={styles.rankText}>{formatRank(entry.rank)}</span>
                    </span>
                  </div>
                  <div className={styles.addressCol}>
                    <span className={styles.walletAddress}>{formatWalletAddress(entry.walletAddress)}</span>
                    {isCurrentUser && <span className={styles.currentUserBadge}>YOU</span>}
                  </div>
                  <div className={styles.scoreCol}>
                    <span className={styles.scoreValue}>
                      {activeTab === "gameCount"
                        ? (entry.finalScore as number).toFixed(2)
                        : formatDXFAmount(entry.finalScore as string)}
                    </span>
                    {entry.multiplier > 1 && (
                      <span className={styles.multiplierBadge}>{entry.multiplier.toFixed(2)}x</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.noEntries}>
              <p>No entries yet for this week</p>
              <span>Be the first to play and claim the top spot!</span>
            </div>
          )}
        </div>

        {/* Sticky User Rank (shown when user entry is out of view) */}
        {showStickyUserRank && renderStickyUserEntry(styles.stickyUserRank)}
      </div>

      {/* Rewards Info (only for current week) */}
      {!isHistoricMode && (
        <div className={styles.rewardsInfo}>
          <div className={styles.rewardsTitle}>
            Weekly Rewards
            <div className={styles.rewardsSubtitle}>{data?.rewardsConfig ? "for Both Rankings" : ""}</div>
          </div>
          {data?.rewardsConfig ? (
            <div className={styles.rewardsList}>
              {(() => {
                // Create a map to merge BNB and DXF rewards by rank
                const mergedRewards = new Map();

                // Add BNB rewards
                data.rewardsConfig.bnbRewards.forEach((reward) => {
                  const key = `${reward.minRank}-${reward.maxRank}`;
                  if (!mergedRewards.has(key)) {
                    mergedRewards.set(key, { ...reward, bnbReward: reward, dxfReward: null });
                  } else {
                    mergedRewards.get(key).bnbReward = reward;
                  }
                });

                // Add DXF rewards
                data.rewardsConfig.dxfRewards.forEach((reward) => {
                  const key = `${reward.minRank}-${reward.maxRank}`;
                  if (!mergedRewards.has(key)) {
                    mergedRewards.set(key, { ...reward, bnbReward: null, dxfReward: reward });
                  } else {
                    mergedRewards.get(key).dxfReward = reward;
                  }
                });

                // Convert to array and sort by minRank
                const sortedRewards = Array.from(mergedRewards.values()).sort((a, b) => a.minRank - b.minRank);

                return sortedRewards.map((mergedReward, index) => (
                  <div key={index} className={styles.rewardTier}>
                    {mergedReward.minRank === mergedReward.maxRank ? (
                      <>
                        {mergedReward.minRank === 1 && "🥇"}
                        {mergedReward.minRank === 2 && "🥈"}
                        {mergedReward.minRank === 3 && "🥉"}
                        {mergedReward.minRank > 3 &&
                          `${mergedReward.minRank}${getOrdinalSuffix(mergedReward.minRank)}:`}
                        {mergedReward.minRank <= 3 &&
                          ` ${mergedReward.minRank}${getOrdinalSuffix(mergedReward.minRank)}:`}
                      </>
                    ) : (
                      `${mergedReward.minRank}${getOrdinalSuffix(mergedReward.minRank)} ~ ${
                        mergedReward.maxRank
                      }${getOrdinalSuffix(mergedReward.maxRank)}:`
                    )}{" "}
                    {mergedReward.bnbReward && formatRewardAmount(mergedReward.bnbReward)}
                    {mergedReward.bnbReward && mergedReward.dxfReward && " + "}
                    {mergedReward.dxfReward && formatRewardAmount(mergedReward.dxfReward)}
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className={styles.comingSoonMessage}>
              <p>🎁 Exciting rewards (BNB + DXF) are being prepared!</p>
              <p>
                Join{" "}
                <a href="https://t.me/DXFCommunity" target="_blank" rel="noreferrer">
                  Telegram
                </a>{" "}
                to stay tuned.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Reward Distribution Status (only for historic mode with completed weeks) */}
      {isHistoricMode && historicData?.rewardDistribution && (
        <RewardDistributionStatus rewardDistribution={historicData.rewardDistribution} />
      )}
    </div>
  );
};

export default WeeklyLeaderboard;
